import React, { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Modal, Space, Typography } from 'antd';
import { MinusOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { ExtraUsersOffer, useBuyExtraUsersMutation } from '../../services/stripeApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { newClientId } from '../../utils/clientId';
import { formatPlanPrice } from '../../utils/currencyHelpers';

const { Text } = Typography;

interface BuyExtraUsersModalProps {
  open: boolean;
  offer: ExtraUsersOffer;
  /** Límite vigente de usuarios; sin él no se muestra cómo queda. */
  currentLimit?: number;
  /** Lo que paga hoy al mes: plan × sucursales + extras. Sin él no se muestra cómo queda. */
  currentMonthly?: number;
  /** Fecha del próximo cobro, o fin de la prueba si la suscripción está en trial. */
  nextChargeDate?: string;
  isTrialing: boolean;
  onClose: () => void;
  onPurchased: (added: number) => void;
}

/** Una compra enviada cuya respuesta no llegó: el reintento la repite con el mismo id. */
interface PendingAttempt {
  purchaseId: string;
  quantity: number;
  expected: number;
}

const usersLabel = (n: number) => `${n} usuario${n === 1 ? '' : 's'}`;

/**
 * Compra de usuarios adicionales. El cargo va a la suscripción del plan con prorrateo,
 * igual que cambiar de plan: hoy no se cobra nada y el lugar queda disponible al momento.
 *
 * Tiene que quedar montado entre aperturas (la página no lo desmonta al cerrarlo): si la
 * respuesta de una compra se pierde, el intento pendiente sobrevive a cerrar y reabrir, y
 * el reintento va con el mismo id. Con uno nuevo, el backend no podría saber que es la
 * misma compra y cobraría otra unidad.
 */
const BuyExtraUsersModal: React.FC<BuyExtraUsersModalProps> = ({
  open,
  offer,
  currentLimit,
  currentMonthly,
  nextChargeDate,
  isTrialing,
  onClose,
  onPurchased,
}) => {
  const { message } = App.useApp();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [buyExtraUsers, { isLoading }] = useBuyExtraUsersMutation();
  const [quantity, setQuantity] = useState(1);
  // Lo que había al abrir la compra, NO lo último que trajo la oferta: la oferta se
  // refresca sola tras cada intento y la cifra que se confirma es la que el dueño vio.
  const [expectedCurrent, setExpectedCurrent] = useState(offer.currentQuantity);
  const [conflict, setConflict] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const pendingRef = useRef<PendingAttempt | null>(null);

  useEffect(() => {
    if (!open) return;
    const pending = pendingRef.current;
    if (pending) {
      // Una compra que no se confirmó: se retoma tal cual, para que el reintento no cobre
      // otra vez aunque el dueño haya cerrado la ventana.
      setQuantity(pending.quantity);
      setExpectedCurrent(pending.expected);
      setResuming(true);
    } else {
      setQuantity(1);
      setExpectedCurrent(offer.currentQuantity);
      setResuming(false);
    }
    setConflict(null);
    // Solo al abrir: la oferta refrescada a media compra no debe mover lo esperado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const max = Math.max(1, offer.maxPerPurchase);
  const unit = offer.unitAmount ?? 0;
  const currency = (offer.currency ?? 'mxn').toUpperCase();
  const addedUsers = quantity * Math.max(1, offer.extraUsersPerUnit);
  const monthlyIncrease = quantity * unit;
  const perInterval = offer.interval === 'year' ? 'al año' : 'al mes';

  const setClamped = (value: number) => {
    const next = Math.min(max, Math.max(1, value));
    // Otra cantidad es otra compra: el intento pendiente ya no se repite. Si sí se había
    // cobrado, el backend lo nota (la cantidad ya no es la esperada) y avisa.
    if (pendingRef.current && pendingRef.current.quantity !== next) {
      pendingRef.current = null;
      setResuming(false);
    }
    setQuantity(next);
  };

  const handleBuy = async () => {
    const attempt = pendingRef.current ?? { purchaseId: newClientId(), quantity, expected: expectedCurrent };
    pendingRef.current = attempt;
    try {
      await buyExtraUsers({
        purchaseId: attempt.purchaseId,
        quantity: attempt.quantity,
        expectedCurrentQuantity: attempt.expected,
      }).unwrap();
      pendingRef.current = null;
      setResuming(false);
      message.success(`Listo: ya puedes dar de alta ${usersLabel(addedUsers)} más`);
      onPurchased(addedUsers);
    } catch (error: any) {
      if (error?.status === 409) {
        // Cambió por otro lado (otra compra, soporte): no se cobró nada. Se muestra la
        // cifra real y se confirma de nuevo sobre ella, como compra nueva.
        pendingRef.current = null;
        setResuming(false);
        if (typeof error?.data?.currentQuantity === 'number') {
          setExpectedCurrent(error.data.currentQuantity);
        }
        setConflict(error?.data?.message || 'Tus usuarios adicionales cambiaron. Revisa y vuelve a intentarlo.');
        return;
      }
      // Cualquier otro error deja el intento pendiente: repetirlo con el mismo id es seguro.
      message.error(
        error?.data?.message
          || 'No pudimos confirmar la compra. Revisa tu conexión y vuelve a intentarlo: no se cobrará dos veces.'
      );
    }
  };

  const rows: Array<{ label: string; from: string; to: string }> = [
    { label: 'Usuarios adicionales', from: String(expectedCurrent), to: String(expectedCurrent + quantity) },
  ];
  if (currentLimit != null) {
    rows.push({ label: 'Límite de usuarios', from: String(currentLimit), to: String(currentLimit + addedUsers) });
  }
  // currentMonthly es mensual: sumarle un extra anual daría una cifra que no es ningún cobro.
  if (currentMonthly != null && (offer.interval == null || offer.interval === 'month')) {
    rows.push({
      label: 'Tu pago al mes',
      from: formatPlanPrice(currentMonthly),
      to: `${formatPlanPrice(currentMonthly + monthlyIncrease)} ${currency}`,
    });
  }

  const formattedDate = nextChargeDate ? formatInTimeZone(nextChargeDate, 'DD/MM/YYYY') : null;

  return (
    <Modal
      open={open}
      onCancel={isLoading ? undefined : onClose}
      maskClosable={!isLoading}
      width={460}
      destroyOnClose
      title={
        <Space size={8}>
          <UserAddOutlined />
          <span>Comprar usuarios adicionales</span>
        </Space>
      }
      footer={
        <Space>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="primary" icon={<UserAddOutlined />} loading={isLoading} onClick={handleBuy}>
            {resuming ? 'Reintentar compra' : `Comprar ${usersLabel(quantity)}`}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {resuming && (
          <Alert
            type="info"
            showIcon
            message={`Tu compra de ${usersLabel(quantity)} no se confirmó. Si la vuelves a enviar no se cobra dos veces.`}
          />
        )}

        <div>
          <Text>¿Cuántos usuarios quieres agregar?</Text>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 12 }}>
            <Button
              shape="circle"
              size="large"
              icon={<MinusOutlined />}
              onClick={() => setClamped(quantity - 1)}
              disabled={quantity <= 1 || isLoading}
              aria-label="Uno menos"
            />
            <Text
              strong
              aria-live="polite"
              aria-label="Usuarios a agregar"
              style={{ minWidth: 48, textAlign: 'center', fontSize: 28, fontVariantNumeric: 'tabular-nums' }}
            >
              {quantity}
            </Text>
            <Button
              shape="circle"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setClamped(quantity + 1)}
              disabled={quantity >= max || isLoading}
              aria-label="Uno más"
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Text type="secondary">
              {formatPlanPrice(unit)} {currency} {perInterval} por usuario
            </Text>
          </div>
        </div>

        {/* Al retomar no se sabe si la compra ya se aplicó (la pantalla ya pudo haberla
            contado): un "antes → después" podría sumar dos veces lo mismo. */}
        {!resuming && (
          <div
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              padding: '12px 16px',
            }}
          >
            {rows.map((row) => (
              <div
                key={row.label}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}
              >
                <Text type="secondary">{row.label}</Text>
                <Text style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {row.from} → <Text strong>{row.to}</Text>
                </Text>
              </div>
            ))}
          </div>
        )}

        <Text type="secondary" style={{ fontSize: 13 }}>
          {isTrialing
            ? `Los lugares quedan listos al confirmar. Hoy no se cobra nada: tu primer cobro${formattedDate ? ` (${formattedDate})` : ''} ya incluirá ${formatPlanPrice(monthlyIncrease)} ${currency} ${perInterval} por ${usersLabel(quantity)}.`
            : `Los lugares quedan listos al confirmar. Hoy no se cobra nada: en tu próxima factura${formattedDate ? ` (${formattedDate})` : ''} se suman ${formatPlanPrice(monthlyIncrease)} ${currency} ${perInterval}, más la parte proporcional de los días que quedan de este periodo.`}
        </Text>

        {offer.hasDiscount && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            Tu suscripción tiene un descuento. Si aplica también a los usuarios adicionales, Stripe lo resta del cobro.
          </Text>
        )}

        {conflict && <Alert type="warning" showIcon message={conflict} />}
      </Space>
    </Modal>
  );
};

export default BuyExtraUsersModal;
